import { Injectable } from '@angular/core';
import { ApiBaseService } from './api-base.service';
import { HttpClient, HttpParams } from '@angular/common/http';
import { ErrorService } from '../error/error.service';
import { NgxSpinnerService } from 'ngx-spinner';
import { environment } from 'src/environments/environment';
import { Observable } from 'rxjs';
import { map, share } from 'rxjs/operators';
import { CaseSessionSearchParams } from 'src/app/shared/models/hearings/caseSessionSearchParams';
import { CaseSession, CaseSessionWrite } from 'src/app/shared/models/hearings/caseSession';
import { SearchParams } from 'src/app/shared/models/searchParams';
import { PostponeCloseCause } from 'src/app/shared/models/hearings/postponeCloseCause';
import {
    CaseSessionSearchResults,
    ExpertSearchResults,
    ExpertSessionSearchResults,
    LawsuitSearchResults,
    PostponeCauseSearchResults,
    SessionNoteSearchResults
} from 'src/app/shared/models/hearings/hearingSearchResults';
import { Expert } from 'src/app/shared/models/hearings/expert';
import { SessionNote } from 'src/app/shared/models/hearings/sessionNote';
import { ExpertSession, ExpertSessionWrite } from 'src/app/shared/models/hearings/expertSession';
import { ExpertSessionSearchParams } from 'src/app/shared/models/hearings/expertSessionSearchParams';
import { RollsSearchResult } from './../../../shared/models/hearings/roll';


@Injectable({
    providedIn: 'root'
})
export class HearingsApiService extends ApiBaseService {

    baseUrl = environment.backend_host + '/api/hearings';

    constructor(
        public http: HttpClient,
        protected errorService: ErrorService,
        protected spinner: NgxSpinnerService) {
        super(spinner, errorService);
    }

    /***********************
    // Hearings or Sessions
    /***********************/

    // Case Sessions
    public getCaseSessions = (params: CaseSessionSearchParams): Observable<CaseSessionSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/case-sessions`, { params: p })
            .pipe(map(this.deserializeAs(CaseSessionSearchResults)));
    }

    public getCaseSession = (id: number): Observable<CaseSession> => {
        return this.http.get<any>(`${this.baseUrl}/case-sessions/${id}`)
            .pipe(map(this.deserializeAs(CaseSession)));
    }

    public createCaseSession = (c: CaseSessionWrite): Observable<CaseSession> => {
        return this.http.post<any>(`${this.baseUrl}/case-sessions`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseSession)));
    }

    public updateCaseSession = (c: CaseSessionWrite): Observable<CaseSession> => {
        return this.http.put<any>(`${this.baseUrl}/case-sessions/${c.id}`, this.serialize(c))
            .pipe(map(this.deserializeAs(CaseSession)));
    }

    public deleteCaseSession = (c: CaseSession): Observable<CaseSession> => {
        return this.http.delete<any>(`${this.baseUrl}/case-sessions/${c.id}`)
            .pipe(share());
    }

    public getOverdueCaseSessions = (params: CaseSessionSearchParams): Observable<CaseSessionSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/overdue-hearings`, { params: p })
            .pipe(map(this.deserializeAs(CaseSessionSearchResults)));
    }

    // Expert Sessions
    public getExpertSessions = (params: ExpertSessionSearchParams): Observable<ExpertSessionSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/expert-sessions`, { params: p })
            .pipe(map(this.deserializeAs(ExpertSessionSearchResults)));
    }

    public getExpertSession = (id: number): Observable<ExpertSession> => {
        return this.http.get<any>(`${this.baseUrl}/expert-sessions/${id}`)
            .pipe(map(this.deserializeAs(ExpertSession)));
    }

    public createExpertSession = (e: ExpertSessionWrite): Observable<ExpertSessionWrite> => {
        return this.http.post<any>(`${this.baseUrl}/expert-sessions`, this.serialize(e))
            .pipe(map(this.deserializeAs(ExpertSessionWrite)));
    }

    public updateExpertSession = (e: ExpertSessionWrite): Observable<ExpertSession> => {
        return this.http.put<any>(`${this.baseUrl}/expert-sessions/${e.id}`, this.serialize(e))
            .pipe(map(this.deserializeAs(ExpertSession)));
    }

    public deleteExpertSession = (e: ExpertSession): Observable<ExpertSession> => {
        return this.http.delete<any>(`${this.baseUrl}/expert-sessions/${e.id}`)
            .pipe(share());
    }

    // Experts
    public getExperts = (params: SearchParams): Observable<ExpertSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/experts`, { params: p })
            .pipe(map(this.deserializeAs(ExpertSearchResults)));
    }

    public getExpert = (id: number): Observable<Expert> => {
        return this.http.get<any>(`${this.baseUrl}/experts/${id}`)
            .pipe(map(this.deserializeAs(Expert)));
    }

    public createExpert = (c: Expert): Observable<Expert> => {
        return this.http.post<any>(`${this.baseUrl}/experts`, this.serialize(c))
            .pipe(map(this.deserializeAs(Expert)));
    }

    public updateExpert = (c: Expert): Observable<Expert> => {
        return this.http.put<any>(`${this.baseUrl}/experts/${c.id}`, this.serialize(c))
            .pipe(map(this.deserializeAs(Expert)));
    }

    public deleteExpert = (c: Expert): Observable<Expert> => {
        return this.http.delete<any>(`${this.baseUrl}/experts/${c.id}`)
            .pipe(share());
    }

    // Postpone Causes
    public getPostponeCauses = (params: SearchParams): Observable<PostponeCauseSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/postpone-causes`, { params: p })
            .pipe(map(this.deserializeAs(PostponeCauseSearchResults)));
    }

    public getPostponeCause = (id: number): Observable<PostponeCloseCause> => {
        return this.http.get<any>(`${this.baseUrl}/postpone-causes/${id}`)
            .pipe(map(this.deserializeAs(PostponeCloseCause)));
    }

    public createPostponeCause = (c: PostponeCloseCause): Observable<PostponeCloseCause> => {
        return this.http.post<any>(`${this.baseUrl}/postpone-causes`, this.serialize(c))
            .pipe(map(this.deserializeAs(PostponeCloseCause)));
    }

    public updatePostponeCause = (c: PostponeCloseCause): Observable<PostponeCloseCause> => {
        return this.http.put<any>(`${this.baseUrl}/postpone-causes/${c.id}`, this.serialize(c))
            .pipe(map(this.deserializeAs(PostponeCloseCause)));
    }

    public deletePostponeCause = (c: PostponeCloseCause): Observable<PostponeCloseCause> => {
        return this.http.delete<any>(`${this.baseUrl}/postpone-causes/${c.id}`)
            .pipe(share());
    }

    // Postpone Causes
    public getSessionNotes = (params: SearchParams): Observable<SessionNoteSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/session-notes`, { params: p })
            .pipe(map(this.deserializeAs(SessionNoteSearchResults)));
    }

    public getSessionNote = (id: number): Observable<SessionNote> => {
        return this.http.get<any>(`${this.baseUrl}/session-notes/${id}`)
            .pipe(map(this.deserializeAs(SessionNote)));
    }

    public createSessionNote = (c: SessionNote): Observable<SessionNote> => {
        return this.http.post<any>(`${this.baseUrl}/session-notes`, this.serialize(c))
            .pipe(map(this.deserializeAs(SessionNote)));
    }

    public updateSessionNote = (c: SessionNote): Observable<SessionNote> => {
        return this.http.put<any>(`${this.baseUrl}/session-notes/${c.id}`, this.serialize(c))
            .pipe(map(this.deserializeAs(SessionNote)));
    }

    public deleteSessionNote = (c: SessionNote): Observable<SessionNote> => {
        return this.http.delete<any>(`${this.baseUrl}/session-notes/${c.id}`)
            .pipe(share());
    }


    // Rolls

    public getRoll = (params: CaseSessionSearchParams): Observable<RollsSearchResult> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/roll`, { params: p })
            .pipe(map(this.deserializeAs(RollsSearchResult)));
    }

    public getCaseRolls = (params: CaseSessionSearchParams): Observable<CaseSession[]> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/session-roll`, { params: p })
            .pipe(map(this.deserializeArrayAs(CaseSession)));
    }
    public getExpertRolls = (params: ExpertSessionSearchParams): Observable<ExpertSession[]> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/expert-roll`, { params: p })
            .pipe(map(this.deserializeArrayAs(ExpertSession)));
    }


    public getLawsuits = (params: SearchParams): Observable<LawsuitSearchResults> => {
        const p = this.serializeParamsObj(params, new HttpParams());
        return this.http.get<any>(`${this.baseUrl}/lawsuits`, { params: p })
            .pipe(map(this.deserializeAs(LawsuitSearchResults)));
    }
}